import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Stack,
  Paper,
  Grid,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GiftProduct, GiftProductType, IntegrationType } from '../../../../models';
import { formatNumber } from '../../../../utils';
import PlaceSelectionModal from '../places/PlaceSelectionModal';
import ProductPlaceItem from './ProductPlaceItem';
import { CurrencyInput } from '../../../../components/mui/inputs';

interface PlaceConfig {
  placeId: string;
  placeName: string;
  integration?: string | null;
  partnerValue: number | null;
  hasProductPlace: boolean;
  rtiCode?: string | null;
  productRtiCode?: string | null;
  isEvoActive?: boolean;
  discountPercentage?: number | null;
}

interface ProductConfigCardProps {
  product: GiftProduct;
  productIndex: number;
  placesConfigs: PlaceConfig[];
  onAddPlace: (placeId: string) => void;
  onAddAllPlaces?: () => void;
  onDeletePlace: (placeId: string) => void;
  onUpdatePartnerValue: (placeId: string, value: number) => void;
  onUpdateRtiCode: (placeId: string, value: string) => void;
  onUpdateEvoActive: (placeId: string, value: boolean) => void;
  onUpdateTrevoServiceId: (placeId: string, value: number | null) => void;
  onUpdateDiscountPercentage?: (placeId: string, value: number) => void;
  fuels?: any[];
  isLoading?: boolean;
  alreadySelectedPlaces?: string[];
  availablePlacesCount?: number;
}

const ProductConfigCard: React.FC<ProductConfigCardProps> = ({
  product,
  productIndex,
  placesConfigs,
  onAddPlace,
  onAddAllPlaces,
  onDeletePlace,
  onUpdatePartnerValue,
  onUpdateRtiCode,
  onUpdateEvoActive,
  onUpdateTrevoServiceId,
  onUpdateDiscountPercentage,
  fuels = [],
  isLoading = false,
  alreadySelectedPlaces = [],
  availablePlacesCount = 0,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkPartnerValue, setBulkPartnerValue] = useState<number>(0);
  const [bulkRtiCode, setBulkRtiCode] = useState<string>('');
  const [bulkDiscountPercentage, setBulkDiscountPercentage] = useState<number>(0);

  const getProductName = (): string => {
    if (product.type === GiftProductType.Virtual) {
      return product.productName || 'Produto Virtual';
    } else if (product.type === GiftProductType.Existing) {
      return product.productName || `Produto ID: ${product.productId}`;
    } else if (product.type === GiftProductType.FuelDiscount) {
      return 'Desconto no Combustível';
    } else if (product.type === GiftProductType.PredictionTicket) {
      return `Direito ao palpite${product.bolaoName ? `: ${product.bolaoName}` : ''}`;
    } else if (product.type === GiftProductType.SpinTicket) {
      return `Giro na roleta${product.roletaName ? `: ${product.roletaName}` : ''}`;
    }
    return `Produto ${productIndex + 1}`;
  };

  const getProductDetails = (): string => {
    if (product.type === GiftProductType.Virtual) {
      const details: string[] = [];
      if (product.productValue) {
        details.push(`Valor: ${formatNumber(product.productValue, { style: 'currency', currency: 'BRL' })}`);
      }
      if (product.discountPercentage) {
        details.push(`Desconto: ${product.discountPercentage}%`);
      }
      return details.join(' • ');
    } else if (product.type === GiftProductType.Existing) {
      return product.productValue ? `Valor: ${formatNumber(product.productValue, { style: 'currency', currency: 'BRL' })}` : 'Produto do catálogo';
    } else if (product.type === GiftProductType.FuelDiscount) {
      return product.discountId ? 'Vinculado a desconto de recompensa' : 'Sem desconto vinculado';
    } else if (product.type === GiftProductType.PredictionTicket) {
      return product.bolaoId ? 'Vinculado a bolão (sem cupom — gera ticket de palpite)' : 'Bolão não selecionado';
    } else if (product.type === GiftProductType.SpinTicket) {
      return product.roletaId ? 'Vinculado a uma roleta (sem cupom — libera giros)' : 'Roleta não selecionada';
    }
    return '';
  };

  const getProductTypeLabel = (): string => {
    if (product.type === GiftProductType.Virtual) return 'Virtual';
    if (product.type === GiftProductType.Existing) return 'Catálogo';
    if (product.type === GiftProductType.FuelDiscount) return 'Desconto';
    if (product.type === GiftProductType.PredictionTicket) return 'Bolão';
    if (product.type === GiftProductType.DrawingNumber) return 'Sorteio';
    if (product.type === GiftProductType.SpinTicket) return 'Roleta';
    return 'Produto';
  };

  const handleAddPlace = (selectedPlace: any) => {
    onAddPlace(selectedPlace.id);
    setModalOpen(false);
  };

  const handleApplyBulkValues = () => {
    placesConfigs.forEach((placeConfig) => {
      // Só atualiza se não for automático
      if (!placeConfig.hasProductPlace && bulkPartnerValue > 0) {
        onUpdatePartnerValue(placeConfig.placeId, bulkPartnerValue);
      }

      // Atualizar RTI code se o produto for RTI (pula locais automáticos herdados do catálogo)
      if (product.integrationType === IntegrationType.rti && bulkRtiCode.trim() && !placeConfig.productRtiCode) {
        onUpdateRtiCode(placeConfig.placeId, bulkRtiCode);
      }

      // Atualizar discount percentage se for produto virtual
      if (product.type === GiftProductType.Virtual && onUpdateDiscountPercentage && bulkDiscountPercentage > 0) {
        onUpdateDiscountPercentage(placeConfig.placeId, bulkDiscountPercentage);
      }
    });

    // Limpar campos após aplicar
    setBulkPartnerValue(0);
    setBulkRtiCode('');
    setBulkDiscountPercentage(0);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          '&:before': { display: 'none' },
          mb: 1.5,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: '#fafafa',
            '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1}>
              <Typography variant="body1" fontWeight={600}>
                {getProductName()}
              </Typography>
              <Chip
                label={getProductTypeLabel()}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              {placesConfigs.length > 0 && (
                <Chip
                  label={`${placesConfigs.length} ${placesConfigs.length === 1 ? 'local' : 'locais'}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            {getProductDetails() && (
              <Typography variant="body2" color="text.secondary">
                {getProductDetails()}
              </Typography>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              {product.type === GiftProductType.FuelDiscount
                ? 'Postos de Resgate para este Desconto'
                : 'Locais de Resgate para este Produto'}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              {product.type === GiftProductType.FuelDiscount
                ? 'Adicione os postos onde este desconto pode ser utilizado.'
                : 'Adicione os locais onde este produto pode ser resgatado e configure o valor do parceiro.'}
            </Typography>

            {/* Seção de bulk values não aparece para FuelDiscount (não há valor do parceiro) */}
            {placesConfigs.length > 1 && product.type !== GiftProductType.FuelDiscount && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: 'primary.lighter',
                  border: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <ContentCopyIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                    Aplicar valores em todos os locais
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {(() => {
                    // Conta quantos inputs estão visíveis (os três possíveis nesta linha)
                    const visibleInputs = [
                      true, // Valor do parceiro sempre visível enquanto o card estiver renderizado
                      product.integrationType === IntegrationType.rti,
                      product.type === GiftProductType.Virtual && onUpdateDiscountPercentage,
                    ].filter(Boolean).length;

                    // Divide a largura em partes iguais
                    const mdCol = visibleInputs > 0 ? Math.floor(12 / visibleInputs) : 12;

                    return (
                      <>
                        <Grid size={{ xs: 12, md: mdCol }}>
                          <CurrencyInput
                            fullWidth
                            size="small"
                            label="Valor do parceiro"
                            value={bulkPartnerValue}
                            onChange={setBulkPartnerValue}
                            helperText="Será aplicado apenas em locais manuais"
                          />
                        </Grid>

                        {product.integrationType === IntegrationType.rti && (
                          <Grid size={{ xs: 12, md: mdCol }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Código RTI"
                              value={bulkRtiCode}
                              onChange={(e) => setBulkRtiCode(e.target.value)}
                              helperText="Código de integração RTI"
                            />
                          </Grid>
                        )}

                        {product.type === GiftProductType.Virtual && onUpdateDiscountPercentage && (
                          <Grid size={{ xs: 12, md: mdCol }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Desconto (%)"
                              type="number"
                              value={bulkDiscountPercentage}
                              onChange={(e) => setBulkDiscountPercentage(parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                              helperText="Percentual de desconto"
                            />
                          </Grid>
                        )}
                      </>
                    );
                  })()}

                  <Grid size={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleApplyBulkValues}
                      disabled={bulkPartnerValue === 0 && !bulkRtiCode.trim() && bulkDiscountPercentage === 0}
                      sx={{ height: '40px' }}
                    >
                      Aplicar a Todos
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {placesConfigs.length > 0 && (
              <Box mb={2}>
                {placesConfigs.map((placeConfig) => (
                  <ProductPlaceItem
                    key={placeConfig.placeId}
                    placeConfig={placeConfig}
                    product={product}
                    onDelete={() => onDeletePlace(placeConfig.placeId)}
                    onUpdatePartnerValue={(value) => onUpdatePartnerValue(placeConfig.placeId, value)}
                    onUpdateRtiCode={(value) => onUpdateRtiCode(placeConfig.placeId, value)}
                    onUpdateEvoActive={(value) => onUpdateEvoActive(placeConfig.placeId, value)}
                    onUpdateTrevoServiceId={(value) => onUpdateTrevoServiceId(placeConfig.placeId, value)}
                    onUpdateDiscountPercentage={onUpdateDiscountPercentage ? (value) => onUpdateDiscountPercentage(placeConfig.placeId, value) : undefined}
                    fuels={fuels}
                  />
                ))}
              </Box>
            )}

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
                onClick={() => setModalOpen(true)}
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  flex: 1,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'primary.main',
                    bgcolor: 'primary.lighter',
                  },
                }}
              >
                {isLoading
                  ? 'Carregando...'
                  : product.type === GiftProductType.FuelDiscount
                    ? 'Adicionar Posto'
                    : 'Adicionar Local'}
              </Button>

              {onAddAllPlaces && availablePlacesCount > alreadySelectedPlaces.length && (
                <Button
                  variant="contained"
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <AddLocationAltIcon />}
                  onClick={onAddAllPlaces}
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    flex: 1,
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {isLoading
                    ? 'Adicionando...'
                    : product.type === GiftProductType.FuelDiscount
                      ? 'Adicionar Todos os Postos'
                      : 'Adicionar Todos'}
                </Button>
              )}
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>

      <PlaceSelectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleAddPlace}
        alreadySelected={alreadySelectedPlaces}
      />
    </>
  );
};

export default ProductConfigCard;
